import { forwardRef, PropsWithChildren, Ref, useRef } from "react";
import {
  Animated,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import BMView from "../../../components/View/BMView";

type Props = {
  headerContent?: React.JSX.Element;
  footerContent?: React.JSX.Element;
  headerStyle?: StyleProp<ViewStyle>;
  footerStyle?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  scrollViewContentStyle?: StyleProp<ViewStyle>;
  onScroll?(event: NativeSyntheticEvent<NativeScrollEvent>): void;
  scrollEventThrottle?: number;
  showsHorizontalScrollIndicator?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  hasInsets?: boolean;
};

function StickyContainer(
  {
    scrollEventThrottle = 16,
    showsHorizontalScrollIndicator = false,
    ...props
  }: PropsWithChildren<Props>,
  ref: Ref<ScrollView>
) {
  const {
    headerContent,
    footerContent,
    onScroll,
    children,
    headerStyle,
    footerStyle,
    contentStyle,
    containerStyle,
    scrollViewContentStyle,
    hasInsets,
  } = props;
  const insets = useSafeAreaInsets();
  const scrollOffsetY = useRef(new Animated.Value(0)).current;
  function handleOnScroll(e: NativeSyntheticEvent<NativeScrollEvent>) {
    Animated.event([{ nativeEvent: { contentOffset: { y: scrollOffsetY } } }], {
      useNativeDriver: false,
    })(e);
    onScroll?.(e);
  }
  return (
    <View
      style={[
        styles.container,
        hasInsets && { paddingTop: insets.top },
        containerStyle,
      ]}
    >
      <BMView style={[headerStyle, styles.header]}>{headerContent}</BMView>
      <ScrollView
        ref={ref}
        style={contentStyle}
        contentContainerStyle={scrollViewContentStyle}
        scrollEventThrottle={scrollEventThrottle}
        onScroll={handleOnScroll}
        showsHorizontalScrollIndicator={showsHorizontalScrollIndicator}
      >
        {children}
      </ScrollView>
      <BMView style={footerStyle}>{footerContent}</BMView>
    </View>
  );
}

export default forwardRef(StickyContainer);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    margin: 0,
  },
  header: {
    zIndex: 1,
  },
});
